import { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip, alpha, ToggleButton, ToggleButtonGroup, TextField, InputAdornment, Tooltip, IconButton, Autocomplete } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import { useChart } from 'src/components/chart';
import Chart from 'src/components/chart/chart';
import useDemoMode, { buildPracticeNameMasker } from 'src/hooks/useDemoMode';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import moment from 'moment';
// Matrix drilldown uses TreatmentLeadPopup (transition basis, same query as the cell +
// the Overview funnel) — NOT the legacy SourceLeadPopup — so cell == popup reconciles.
import TreatmentLeadPopup from './TreatmentLeadPopup';

export const OTHERS_TOOLTIP = 'Mixed statuses — open a specific status to see leads';

const STATUS_ORDER = [
    'NEW',
    'CONSULTATION_BOOKED',
    'CONSULTATION_ATTENDED',
    'TREATMENT_STARTED',
    'CLOSED',
    'OTHERS',
];

const STATUS_LABELS = {
    NEW: 'New Enquiries',
    CONSULTATION_BOOKED: 'Consultation Booked',
    CONSULTATION_ATTENDED: 'Consultation Attended',
    TREATMENT_STARTED: 'Treatment Started',
    CLOSED: 'Completed Treatment',
    OTHERS: 'Others',
};

// Status palette — lead-stage colour system shared with chips / KPI cards / funnel bars.
const STATUS_COLORS = {
    NEW: '#3b82f6',
    CONSULTATION_BOOKED: '#8b5cf6',
    CONSULTATION_ATTENDED: '#f59e0b',
    TREATMENT_STARTED: '#10b981',
    CLOSED: '#64748b',
    OTHERS: '#94a3b8',
};

export default function TreatmentStatusMatrix({ data, practices, selectedPracticeIds, dateRange }) {
    const [demoMode] = useDemoMode();
    const buildExportName = useReportExportFilename();
    const maskName = useMemo(() => buildPracticeNameMasker(practices, demoMode), [practices, demoMode]);
    const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'flat'
    const [statusFilter, setStatusFilter] = useState([]);
    // Multi-select treatment filter: empty = show all treatments.
    const [treatmentFilter, setTreatmentFilter] = useState([]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupConfig, setPopupConfig] = useState({
        practiceId: null,
        status: 'All',
        statusLabel: '',
        categoryId: null,
        treatmentName: '',
        title: '',
    });

    // Get unique treatments from data
    const allTreatments = useMemo(() => {
        if (!data?.length) return [];
        return [...new Set(data.map((item) => item.categoryName).filter(Boolean))].sort();
    }, [data]);

    // Build rows for DataGrid - tree data structure
    const { rows, treeRows } = useMemo(() => {
        const getCellData = (treatment, status, practiceId) =>
            data.find(
                (item) =>
                    item.categoryName === treatment &&
                    item.status === status &&
                    String(item.practiceId) === String(practiceId)
            );

        const flatResult = [];
        const treeResult = [];
        let id = 0;

        allTreatments.forEach((treatment) => {
            const treatmentItem = data.find((d) => d.categoryName === treatment && d.color);
            const treatmentColor = treatmentItem?.color || '#e0e0e0';
            const treatmentCategoryId = treatmentItem?.categoryId;

            // Calculate treatment totals across all statuses
            let treatmentTotalCount = 0;
            let treatmentTotalRevenue = 0;

            const statusRows = [];

            STATUS_ORDER.forEach((status) => {
                let totalCount = 0;
                let totalRevenue = 0;
                const row = {
                    id,
                    treatment,
                    treatmentColor,
                    treatmentCategoryId,
                    status,
                    statusLabel: STATUS_LABELS[status] || status,
                    statusColor: STATUS_COLORS[status] || '#64748b',
                    isGroup: false,
                    path: [treatment, STATUS_LABELS[status]],
                    // Per-practice PracticeTreatment id for the cell — the drilldown MUST
                    // use the CLICKED practice's own treatment id, not the row-level
                    // representative (which belongs to whichever practice sorted first and
                    // would 404 against other practices → "No data").
                    treatmentIdByPractice: {},
                };
                id += 1;

                selectedPracticeIds.forEach((practiceId) => {
                    const cellData = getCellData(treatment, status, practiceId);
                    row[`practice_${practiceId}`] = cellData?.count || 0;
                    row.treatmentIdByPractice[practiceId] = cellData?.categoryId ?? null;
                    totalCount += cellData?.count || 0;
                    totalRevenue += cellData?.revenue || 0;
                });

                row.totalCount = totalCount;
                row.totalRevenue = totalRevenue;
                treatmentTotalCount += totalCount;
                treatmentTotalRevenue += totalRevenue;

                flatResult.push(row);
                statusRows.push(row);
            });

            // Create parent row for tree view
            const parentRow = {
                id: `group_${treatment}`,
                treatment,
                treatmentColor,
                treatmentCategoryId,
                status: '',
                statusLabel: '',
                statusColor: '',
                isGroup: true,
                path: [treatment],
                totalCount: treatmentTotalCount,
                totalRevenue: treatmentTotalRevenue,
                treatmentIdByPractice: {},
            };

            // Sum practice columns for parent + carry each practice's treatment id
            // (constant across statuses for a given practice+treatment).
            selectedPracticeIds.forEach((practiceId) => {
                parentRow[`practice_${practiceId}`] = statusRows.reduce(
                    (sum, r) => sum + (r[`practice_${practiceId}`] || 0), 0
                );
                const withId = statusRows.find((r) => r.treatmentIdByPractice?.[practiceId] != null);
                parentRow.treatmentIdByPractice[practiceId] = withId?.treatmentIdByPractice[practiceId] ?? null;
            });

            treeResult.push(parentRow);
            statusRows.forEach((r) => treeResult.push(r));
        });

        return { rows: flatResult, treeRows: treeResult };
    }, [data, allTreatments, selectedPracticeIds]);

    // Apply filters
    const filteredRows = useMemo(() => {
        const sourceRows = viewMode === 'grouped' ? treeRows : rows;
        let filtered = sourceRows;

        // Status filter
        if (statusFilter.length > 0) {
            filtered = filtered.filter((row) => {
                if (row.isGroup) {
                    // Keep parent if any of its statuses match
                    return true;
                }
                return statusFilter.includes(row.status);
            });
        }

        // Treatment multi-select filter (empty = all treatments)
        if (treatmentFilter.length > 0) {
            const selected = new Set(treatmentFilter);
            filtered = filtered.filter((row) => selected.has(row.treatment));
        }

        return filtered;
    }, [viewMode, treeRows, rows, statusFilter, treatmentFilter]);

    const isChartView = viewMode === 'bar' || viewMode === 'line';

    // Chart data — Bar: status mix per treatment (stacked); Line: funnel per
    // treatment across stages. Respects the treatment + status filters, and
    // aggregates counts across all selected practices.
    const chartData = useMemo(() => {
        const statusKeys = STATUS_ORDER.filter((s) => s !== 'OTHERS');
        const activeStatuses = statusFilter.length
            ? statusKeys.filter((s) => statusFilter.includes(s))
            : statusKeys;
        let activeTreatments = treatmentFilter.length
            ? allTreatments.filter((t) => treatmentFilter.includes(t))
            : allTreatments;

        const totals = {};
        data.forEach((d) => {
            if (!activeTreatments.includes(d.categoryName)) return;
            totals[d.categoryName] = totals[d.categoryName] || {};
            totals[d.categoryName][d.status] = (totals[d.categoryName][d.status] || 0) + (d.count || 0);
        });
        const sumFor = (t) => activeStatuses.reduce((s, st) => s + (totals[t]?.[st] || 0), 0);

        // A line per treatment gets unreadable past ~10 — cap to top by volume.
        let capped = false;
        if (viewMode === 'line' && activeTreatments.length > 10) {
            activeTreatments = [...activeTreatments].sort((a, b) => sumFor(b) - sumFor(a)).slice(0, 10);
            capped = true;
        }

        if (viewMode === 'line') {
            return {
                capped,
                categories: activeStatuses.map((s) => STATUS_LABELS[s]),
                series: activeTreatments.map((t) => ({
                    name: t,
                    data: activeStatuses.map((s) => totals[t]?.[s] || 0),
                })),
                colors: activeTreatments.map(
                    (t) => (data.find((d) => d.categoryName === t && d.color)?.color) || '#94a3b8'
                ),
            };
        }
        return {
            capped,
            categories: activeTreatments,
            series: activeStatuses.map((s) => ({
                name: STATUS_LABELS[s],
                data: activeTreatments.map((t) => totals[t]?.[s] || 0),
            })),
            colors: activeStatuses.map((s) => STATUS_COLORS[s]),
        };
    }, [data, allTreatments, treatmentFilter, statusFilter, viewMode]);

    const chartOptions = useChart({
        colors: chartData.colors,
        chart: { stacked: viewMode === 'bar' },
        plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '78%' } },
        stroke: viewMode === 'line' ? { width: 3, curve: 'smooth' } : { width: 0 },
        markers: { size: viewMode === 'line' ? 4 : 0 },
        xaxis: { categories: chartData.categories },
        legend: { position: 'top' },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (val) => `${val} leads` } },
    });

    // Tree data path getter
    const getTreeDataPath = useCallback((row) => row.path, []);

    // Grouping column definition for tree data
    const groupingColDef = useMemo(() => ({
        headerName: 'Treatment / Status',
        width: 280,
        renderCell: (params) => {
            const { row } = params;
            if (row.isGroup) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                            label={row.treatment}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                bgcolor: row.treatmentColor,
                                color: '#333',
                                maxWidth: 200,
                            }}
                        />
                    </Box>
                );
            }
            return (
                <Typography
                    sx={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: row.statusColor,
                        pl: 1,
                    }}
                >
                    {row.statusLabel}
                </Typography>
            );
        },
    }), []);

    // Handle cell click for popup
    const handleCellClick = useCallback((row, practiceId) => {
        if (!row || !practiceId) return;
        if (row.status === 'OTHERS') return; // mixed bucket — not a queryable state
        // Use the CLICKED practice's own PracticeTreatment id — never the row-level
        // representative (which belongs to another practice and returns "No data").
        const treatmentId = row.treatmentIdByPractice?.[practiceId] ?? null;
        if (treatmentId == null) return; // no PracticeTreatment for this practice+treatment
        const practice = practices?.find((p) => String(p.id) === String(practiceId));
        const practiceName = maskName(practice, practiceId);
        const statusLabel = row.isGroup ? 'All Statuses' : (STATUS_LABELS[row.status] || row.status);
        const statusValue = row.isGroup ? 'All' : row.status;

        setPopupConfig({
            practiceId,
            status: statusValue,
            statusLabel,
            categoryId: treatmentId,
            treatmentName: row.treatment,
            title: `${row.treatment} — ${statusLabel} (${practiceName})`,
        });
        setPopupOpen(true);
    }, [practices]);

    // Handle total column click (all practices combined)
    const handleTotalClick = useCallback((row) => {
        if (!row || selectedPracticeIds.length === 0) return;
        if (row.status === 'OTHERS') return; // mixed bucket — not a queryable state
        const statusLabel = row.isGroup ? 'All Statuses' : (STATUS_LABELS[row.status] || row.status);
        const statusValue = row.isGroup ? 'All' : row.status;

        if (selectedPracticeIds.length === 1) {
            // Single practice → the Total column IS that practice; use its own id.
            const onlyPractice = selectedPracticeIds[0];
            const treatmentId = row.treatmentIdByPractice?.[onlyPractice] ?? null;
            if (treatmentId == null) return;
            setPopupConfig({
                practiceId: onlyPractice,
                status: statusValue,
                statusLabel,
                categoryId: treatmentId,
                treatmentName: row.treatment,
                title: `${row.treatment} — ${statusLabel} (Total)`,
            });
            setPopupOpen(true);
        }
    }, [selectedPracticeIds]);

    const handleClosePopup = useCallback(() => {
        setPopupOpen(false);
    }, []);

    // Build columns for DataGrid
    const columns = useMemo(() => {
        const cols = [];

        // In flat mode, add treatment and status columns
        if (viewMode === 'flat') {
            cols.push({
                field: 'treatment',
                headerName: 'Treatment',
                width: 180,
                sortable: true,
                filterable: true,
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        sx={{
                            fontWeight: 500,
                            bgcolor: params.row.treatmentColor,
                            color: '#333',
                            maxWidth: '100%',
                        }}
                    />
                ),
            });
            cols.push({
                field: 'statusLabel',
                headerName: 'Status',
                width: 160,
                sortable: true,
                filterable: true,
                renderCell: (params) => (
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: params.row.statusColor,
                        }}
                    >
                        {params.value}
                    </Typography>
                ),
            });
        }

        // Add practice columns
        selectedPracticeIds.forEach((practiceId) => {
            const practice = practices?.find((p) => String(p.id) === String(practiceId));
            cols.push({
                field: `practice_${practiceId}`,
                headerName: maskName(practice, practiceId),
                flex: 1,
                minWidth: 120,
                align: 'center',
                headerAlign: 'center',
                sortable: true,
                filterable: true,
                type: 'number',
                renderCell: (params) => {
                    const val = params.value || 0;
                    const isGroupRow = params.row.isGroup;
                    const isOthersRow = params.row.status === 'OTHERS';
                    const clickable = val > 0 && !isGroupRow && !isOthersRow;
                    const getBgColor = () => {
                        if (val <= 0) return 'transparent';
                        return isGroupRow ? alpha('#0369a1', 0.15) : '#e0f2fe';
                    };
                    const getFontWeight = () => {
                        if (val <= 0) return 400;
                        return isGroupRow ? 700 : 600;
                    };
                    const chip = (
                        <Chip
                            label={val}
                            size="small"
                            variant={val > 0 ? 'filled' : 'outlined'}
                            onClick={clickable ? (e) => { e.stopPropagation(); handleCellClick(params.row, practiceId); } : undefined}
                            sx={{
                                fontSize: '12px',
                                minWidth: 40,
                                bgcolor: getBgColor(),
                                color: val > 0 ? '#0369a1' : '#94a3b8',
                                fontWeight: getFontWeight(),
                                cursor: clickable ? 'pointer' : 'default',
                                '&:hover': clickable ? {
                                    bgcolor: alpha('#0369a1', 0.25),
                                } : {},
                            }}
                        />
                    );
                    return isOthersRow && val > 0 ? <Tooltip title={OTHERS_TOOLTIP}><span>{chip}</span></Tooltip> : chip;
                },
            });
        });

        // Total column
        cols.push({
            field: 'totalCount',
            headerName: 'Total',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            sortable: true,
            filterable: true,
            type: 'number',
            renderCell: (params) => {
                const val = params.value || 0;
                const clickable = val > 0 && !params.row.isGroup && params.row.status !== 'OTHERS' && selectedPracticeIds.length === 1;
                return (
                    <Typography
                        onClick={clickable ? (e) => { e.stopPropagation(); handleTotalClick(params.row); } : undefined}
                        sx={{
                            fontWeight: params.row.isGroup ? 700 : 600,
                            fontSize: '13px',
                            color: clickable ? '#3b82f6' : 'inherit',
                            cursor: clickable ? 'pointer' : 'default',
                            '&:hover': clickable ? { textDecoration: 'underline' } : {},
                        }}
                    >
                        {val}
                    </Typography>
                );
            },
        });

        // Revenue column
        cols.push({
            field: 'totalRevenue',
            headerName: 'Revenue',
            width: 120,
            align: 'right',
            headerAlign: 'right',
            sortable: true,
            filterable: true,
            type: 'number',
            renderCell: (params) => {
                // Booked/Attended counts come from appointments, which carry no
                // treatment price — show "—" rather than a misleading £0.
                const st = params.row?.status;
                if (st === 'CONSULTATION_BOOKED' || st === 'CONSULTATION_ATTENDED') {
                    return <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>—</Typography>;
                }
                return (
                    <Typography sx={{ fontWeight: params.row.isGroup ? 700 : 600, fontSize: '13px', color: '#10b981' }}>
                        £{(params.value || 0).toLocaleString()}
                    </Typography>
                );
            },
        });

        return cols;
    }, [selectedPracticeIds, practices, viewMode, handleCellClick, handleTotalClick]);

    // Row class for styling
    const getRowClassName = useCallback((params) => {
        if (params.row.isGroup) return 'treatment-group-parent';
        return '';
    }, []);

    // Handle status filter toggle
    const handleStatusFilterToggle = (status) => {
        setStatusFilter((prev) =>
            prev.includes(status)
                ? prev.filter((s) => s !== status)
                : [...prev, status]
        );
    };

    // CSV export handler
    const handleExportCSV = useCallback(() => {
        // Export exactly what the grid shows: filteredRows already reflects the
        // active status-filter chips and search query (and the grouped/flat
        // viewMode). Drop group/parent rows, keeping the leaf data rows.
        const visibleRows = filteredRows.filter((r) => !r.isGroup);

        const practiceNames = selectedPracticeIds.map((pid) => {
            const p = practices?.find((pr) => String(pr.id) === String(pid));
            return maskName(p, pid);
        });

        const headers = ['Treatment', 'Status', ...practiceNames, 'Total', 'Revenue'];
        const csvRows = visibleRows.map((r) => {
            const practiceCols = selectedPracticeIds.map((pid) => r[`practice_${pid}`] || 0);
            return [
                `"${(r.treatment || '').replace(/"/g, '""')}"`,
                `"${(r.statusLabel || '').replace(/"/g, '""')}"`,
                ...practiceCols,
                r.totalCount || 0,
                r.totalRevenue || 0,
            ].join(',');
        });

        const csv = [headers.join(','), ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildExportName('treatment-status-matrix', dateRange, { practiceName: 'all-practices' });
        link.click();
        URL.revokeObjectURL(url);
    }, [filteredRows, selectedPracticeIds, practices, dateRange, buildExportName]);

    if (!data?.length) {
        return (
            <Box
                sx={{
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    p: '24px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    mb: 3,
                }}
            >
                <Typography sx={{ fontSize: '20px', fontWeight: 700, mb: 2 }}>
                    Treatment Status Comparison
                </Typography>
                <Typography sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>
                    No treatment status data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                mb: 3,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                        Treatment Status Comparison
                    </Typography>
                    <Tooltip title="New Enquiries counts leads by creation date. Booked, Attended, Started and Closed count leads by when they transitioned into that stage during the period (same basis as the Overview funnel) — so this matrix reconciles with Overview. Click any cell to see the exact leads.">
                        <IconButton size="small" sx={{ color: '#94a3b8' }}>
                            <Iconify icon="eva:info-outline" width={18} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                        {allTreatments.length} treatments across {selectedPracticeIds.length} practices
                    </Typography>
                    <Tooltip title="Export to CSV">
                        <IconButton onClick={handleExportCSV} size="small" sx={{ color: '#64748b' }}>
                            <Iconify icon="eva:download-outline" width={20} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Filter Bar */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid #e2e8f0',
                    flexWrap: 'wrap',
                }}
            >
                {/* Treatment multi-select filter */}
                <Autocomplete
                    multiple
                    size="small"
                    options={allTreatments}
                    value={treatmentFilter}
                    onChange={(e, newValue) => setTreatmentFilter(newValue)}
                    disableCloseOnSelect
                    autoHighlight
                    limitTags={2}
                    ChipProps={{ size: 'small' }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={treatmentFilter.length ? '' : 'Filter treatments...'}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <InputAdornment position="start" sx={{ ml: 0.5 }}>
                                            <Iconify icon="eva:funnel-fill" width={18} sx={{ color: 'text.disabled' }} />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    sx={{
                        minWidth: 240,
                        maxWidth: 360,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '13px',
                        },
                    }}
                />

                {/* Status Filter Chips */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
                    {STATUS_ORDER.map((status) => {
                        const isActive = statusFilter.includes(status);
                        return (
                            <Chip
                                key={status}
                                label={STATUS_LABELS[status]}
                                size="small"
                                onClick={() => handleStatusFilterToggle(status)}
                                sx={{
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    height: 26,
                                    bgcolor: isActive ? alpha(STATUS_COLORS[status], 0.15) : 'transparent',
                                    color: isActive ? STATUS_COLORS[status] : '#94a3b8',
                                    border: `1px solid ${isActive ? STATUS_COLORS[status] : '#e2e8f0'}`,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: alpha(STATUS_COLORS[status], 0.1),
                                    },
                                }}
                            />
                        );
                    })}
                    {statusFilter.length > 0 && (
                        <Chip
                            label="Clear"
                            size="small"
                            onClick={() => setStatusFilter([])}
                            onDelete={() => setStatusFilter([])}
                            sx={{
                                fontSize: '11px',
                                fontWeight: 600,
                                height: 26,
                                color: '#64748b',
                            }}
                        />
                    )}
                </Box>

                {/* View Mode Toggle */}
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, val) => val && setViewMode(val)}
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            px: 1.5,
                            py: 0.5,
                            fontSize: '12px',
                            fontWeight: 500,
                            border: '1px solid #e2e8f0',
                            '&.Mui-selected': {
                                bgcolor: alpha('#3b82f6', 0.1),
                                color: '#3b82f6',
                                borderColor: '#3b82f6',
                                '&:hover': {
                                    bgcolor: alpha('#3b82f6', 0.15),
                                },
                            },
                        },
                    }}
                >
                    <ToggleButton value="grouped">
                        <Iconify icon="mdi:file-tree" width={16} sx={{ mr: 0.5 }} />
                        Grouped
                    </ToggleButton>
                    <ToggleButton value="flat">
                        <Iconify icon="mdi:table" width={16} sx={{ mr: 0.5 }} />
                        Flat
                    </ToggleButton>
                    <ToggleButton value="bar">
                        <Iconify icon="mdi:chart-bar" width={16} sx={{ mr: 0.5 }} />
                        Bar
                    </ToggleButton>
                    <ToggleButton value="line">
                        <Iconify icon="mdi:chart-line" width={16} sx={{ mr: 0.5 }} />
                        Line
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Chart view (Bar / Line) */}
            {isChartView ? (
                <Box sx={{ px: 2, py: 1.5 }}>
                    {chartData.capped && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                            Showing the top 10 treatments by volume — use the treatment filter to focus on specific treatments.
                        </Typography>
                    )}
                    {chartData.series.length === 0 ? (
                        <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 6 }}>
                            No data for the selected filters.
                        </Typography>
                    ) : (
                        <Chart
                            key={viewMode}
                            type={viewMode === 'bar' ? 'bar' : 'line'}
                            series={chartData.series}
                            options={chartOptions}
                            height={viewMode === 'bar'
                                ? Math.max(360, chartData.categories.length * 34)
                                : 460}
                        />
                    )}
                </Box>
            ) : (
            /* DataGrid */
            <Box
                sx={{
                    height: 600,
                    width: '100%',
                    '& .MuiDataGrid-root': {
                        border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        bgcolor: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid #f1f5f9',
                        py: 1,
                    },
                    '& .MuiDataGrid-row:hover': {
                        bgcolor: alpha('#3b82f6', 0.04),
                    },
                    '& .MuiDataGrid-row.treatment-group-parent': {
                        bgcolor: '#f8fafc',
                        '&:hover': {
                            bgcolor: alpha('#3b82f6', 0.06),
                        },
                    },
                    '& .MuiDataGrid-pinnedColumns': {
                        bgcolor: '#fff',
                        boxShadow: '2px 0 4px rgba(0,0,0,0.05)',
                    },
                    '& .MuiDataGrid-pinnedColumnHeaders': {
                        bgcolor: '#f8fafc',
                    },
                    '& .MuiDataGrid-footerContainer': {
                        borderTop: '1px solid #e2e8f0',
                    },
                    '& .MuiDataGrid-treeDataGroupingCell': {
                        pl: 1,
                    },
                }}
            >
                {viewMode === 'grouped' ? (
                    <DataGridPro
                        key="grouped"
                        rows={filteredRows}
                        columns={columns}
                        treeData
                        getTreeDataPath={getTreeDataPath}
                        groupingColDef={groupingColDef}
                        defaultGroupingExpansionDepth={-1}
                        getRowId={(row) => row.id}
                        disableRowSelectionOnClick
                        hideFooter
                        getRowClassName={getRowClassName}
                        rowHeight={48}
                        columnHeaderHeight={48}
                        density="standard"
                    />
                ) : (
                    <DataGridPro
                        key="flat"
                        rows={filteredRows}
                        columns={columns}
                        disableRowSelectionOnClick
                        hideFooter
                        rowHeight={48}
                        columnHeaderHeight={48}
                        initialState={{
                            pinnedColumns: {
                                left: ['treatment', 'statusLabel'],
                            },
                            sorting: {
                                sortModel: [{ field: 'treatment', sort: 'asc' }],
                            },
                        }}
                        density="standard"
                    />
                )}
            </Box>
            )}

            {/* Lead Detail Popup — TreatmentLeadPopup drills by treatment + state on the
                SAME transition basis as the matrix cell (per-state state_transition_params
                for statuses; created_at for NEW), so cell == popup. */}
            <TreatmentLeadPopup
                open={popupOpen}
                onClose={handleClosePopup}
                practiceId={popupConfig.practiceId}
                status={popupConfig.status}
                treatmentId={popupConfig.categoryId}
                treatmentName={popupConfig.treatmentName}
                categoryName={popupConfig.treatmentName || ''}
                dateRange={{
                    startDate: moment(dateRange?.startDate).format('YYYY-MM-DD'),
                    endDate: moment(dateRange?.endDate).format('YYYY-MM-DD'),
                }}
            />
        </Box>
    );
}

TreatmentStatusMatrix.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            categoryName: PropTypes.string,
            categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            color: PropTypes.string,
            status: PropTypes.string,
            count: PropTypes.number,
            revenue: PropTypes.number,
        })
    ),
    practices: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
        })
    ),
    selectedPracticeIds: PropTypes.arrayOf(PropTypes.string),
    dateRange: PropTypes.shape({
        startDate: PropTypes.instanceOf(Date),
        endDate: PropTypes.instanceOf(Date),
    }),
};

TreatmentStatusMatrix.defaultProps = {
    data: [],
    practices: [],
    selectedPracticeIds: [],
    dateRange: null,
};
